// @ts-nocheck
import React from 'react';
import PropTypes from 'react-proptypes';
import Immutable from 'immutable';
import { Feature } from '@fulcrumapp/fulcrum-core';
import { diff, shouldComponentUpdate } from '../../utils/scu';
import LayerPicker from '../../layer-picker';
import BASEMAPS, { SAFE_DEFAULT_BASEMAP } from '../../layer-picker/basemaps';
import {
  fulcrumJSONLayer,
  fulcrumMBTilesJSONLayer,
  fulcrumMBTilesPNGLayer,
  fulcrumPNGLayer,
  getTileUrl,
  getWmsTileUrl,
} from '../../shared/map/utils/layer-urls';
import { AppContext } from '../../hooks/use-app';
import mapLayersAppliedEvent from '../../services/fetch/user-events/map-layers-applied-event';
import mapLoadedEvent from '../../services/fetch/user-events/map-loaded-event';
import LayerManager from './layer-manager';
import CurrentViewportMarkerManager from './current-viewport-marker-manager';
import RepeatableMarkerManager from './repeatable-marker-manager';
import {
  ZoomInControl, ZoomOutControl, ExtentsControl, LayersControl, LocateControl,
} from './simple-control';
import GeocoderControl from './geocoder-control';
import GridControl from './grid-control';
import OsmAttributionControl from './osm-attribution-control';

export default class MapView extends React.Component {
  static propTypes = {
    center: PropTypes.arrayOf(PropTypes.number),
    cursor: PropTypes.string,
    circleMarkers: PropTypes.instanceOf(Immutable.List),
    editing: PropTypes.bool,
    repeatableMarkers: PropTypes.instanceOf(Immutable.List),
    highlightedRepeatable: PropTypes.instanceOf(Feature),
    onRepeatableMarkerAdded: PropTypes.func,
    currentViewPoint: PropTypes.object,
    className: PropTypes.string,
    lines: PropTypes.instanceOf(Immutable.List),
    popup: PropTypes.object,
    onFeatureClick: PropTypes.func,
    onFeatureMouseOver: PropTypes.func,
    onFeatureMouseOut: PropTypes.func,
    onMapClick: PropTypes.func,
    onMapMoveEnd: PropTypes.func,
    onMarkerClick: PropTypes.func,
    onMarkerMouseOver: PropTypes.func,
    onMarkerMouseOut: PropTypes.func,
    onTrackLineClick: PropTypes.func,
    userLocationEnabled: PropTypes.bool,
    visible: PropTypes.bool.isRequired,
    zoom: PropTypes.number.isRequired,
    bounds: PropTypes.object,
    extents: PropTypes.object,
    dataExtents: PropTypes.array,
    showExtentsControl: PropTypes.bool,
    showGeocoderControl: PropTypes.bool,
    parentView: PropTypes.string.isRequired,
  };

  static defaultProps = {
    center: [0, 0],
    userLocationEnabled: true,
    visible: true,
    zoom: 3,
    cursor: '',
    showExtentsControl: true,
    showGeocoderControl: true,
    editingGeometry: false,
  };

  static contextType = AppContext;

  constructor(props, context) {
    super(props, context);

    // TODO: Can we get away with not lazy-loading these?
    //       We don't know if the `google.maps` object is ready yet.
    this.UtfGrid = import('./utf-grid');
    this.GeolocationMarker = import('./geolocation-marker');
    import('./popup').then((Popup) => { this.Popup = Popup.default; });

    this._geoJSON = {};

    this._shouldComponentUpdate = shouldComponentUpdate.bind(this);

    this._lastBaseMap = null;

    this._watchingLocation = false;

    this.state = {
      showLayerPicker: false,
    };
  }

  componentDidMount() {
    const center = this.props.center || MapView.defaultProps.center;
    const mapOptions = {
      center: { lat: center[0], lng: center[1] },
      zoom: this.props.zoom,
      clickableIcons: false,
      draggableCursor: this.props.cursor,
      disableDefaultUI: true,
      controlSize: 32,
      zoomControl: false,
      rotateControl: true,
      scaleControl: true,
    };

    this.map = new google.maps.Map(this.mapNode, mapOptions);
    this.map.data.setStyle({ clickable: false });
    this.currentViewportMarkerManager = new CurrentViewportMarkerManager(this.map);
    this.repeatableMarkerManager = new RepeatableMarkerManager(this.map,
      this.props.onRepeatableMarkerAdded);
    this.layerManager = new LayerManager(this.map);

    const xyzBasemaps = BASEMAPS.filter((basemap) => basemap.type === 'xyz');
    const layers = {};

    xyzBasemaps.forEach((basemap) => {
      layers[basemap.id] = new google.maps.ImageMapType({
        getTileUrl: getTileUrl(basemap.url),
        tileSize: new google.maps.Size(256, 256),
        maxZoom: basemap.options.maxZoom,
        minZoom: basemap.options.minZoom,
        name: basemap.name,
      });
    });

    Object.keys(layers).forEach((key) => {
      this.map.mapTypes.set(key, layers[key]);
    });

    // this.infoWindow = new google.maps.InfoWindow({maxWidth: 340, disableAutoPan: true});

    if (this.props.showGeocoderControl) {
      this.geocoderControl = new GeocoderControl();
      this.map.controls[google.maps.ControlPosition.LEFT_TOP].push(this.geocoderControl.element);

      const autocomplete = new google.maps.places.Autocomplete(this.geocoderControl.element, { options: { fields: ['geometry'] } });
      autocomplete.bindTo('bounds', this.map);

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();

        if (place.geometry) {
          this.map.fitBounds(place.geometry.viewport);
        }
      });
    }

    const zoomInControl = new ZoomInControl(this.handleZoomInClick);
    this.map.controls[google.maps.ControlPosition.LEFT_TOP].push(zoomInControl.element);

    const zoomOutControl = new ZoomOutControl(this.handleZoomOutClick);
    this.map.controls[google.maps.ControlPosition.LEFT_TOP].push(zoomOutControl.element);

    if (this.props.userLocationEnabled) {
      this.locateControl = new LocateControl(this.handleLocateClick);
      this.map.controls[google.maps.ControlPosition.LEFT_TOP].push(this.locateControl.element);
    }

    if (this.props.showExtentsControl) {
      const extentsControl = new ExtentsControl(this.handleExtentsClick);
      this.map.controls[google.maps.ControlPosition.LEFT_TOP].push(extentsControl.element);
    }

    const layersControl = new LayersControl(this.handleLayerClick);
    this.map.controls[google.maps.ControlPosition.LEFT_TOP].push(layersControl.element);

    this.osmAttributionControl = new OsmAttributionControl();
    this.map.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push(this.osmAttributionControl.element);

    this.gridControl = new GridControl();
    this.map.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push(this.gridControl.element);

    this.updateLayers();

    this.updateCircleMarkers();

    this.repeatableMarkerManager.updateMarkers(this.props.repeatableMarkers);

    this.repeatableMarkerManager.updateHighlighted(this.props.highlightedRepeatable);

    this.updateCurrentViewPoint();

    this.updateLines();

    if (this.props.bounds) {
      this.updateBounds();
    } else if (this.props.extents) {
      this.updateExtents();
    } else if (this.props.dataExtents) {
      this.updateDataExtents();
    } else {
      this.updateCenterAndZoom();
    }

    this.map.addListener('click', this.handleMapClicked);
    this.map.addListener('idle', this.handleMapMoveEnd);

    mapLoadedEvent(
      'Google',
      this.context.layerSettings.googleEngineActiveBasemap.id,
      this.context.layerSettings.activeOverlays,
      this.props.parentView,
    );
  }

  shouldComponentUpdate(nextProps, nextState) {
    if (this._layersLastUpdated !== this.context.layerSettings.lastUpdated) {
      return true;
    }

    return this._shouldComponentUpdate(nextProps, nextState);
  }

  componentDidUpdate(prevProps, prevState) {
    this.map.setOptions({ draggableCursor: this.props.cursor });

    // when we download a GeoJSON file we need to force a reload
    if (this._layersLastUpdated !== this.context.layerSettings.lastUpdated) {
      this.updateLayers();
    }

    if (diff(prevProps.circleMarkers, this.props.circleMarkers)) {
      this.updateCircleMarkers();
    }

    if ((this.repeatableMarkerManager.isEmpty && this.props.repeatableMarkers)
        || diff(prevProps.repeatableMarkers, this.props.repeatableMarkers)) {
      this.repeatableMarkerManager.updateMarkers(this.props.repeatableMarkers);
    }

    this.repeatableMarkerManager.updateHighlighted(this.props.highlightedRepeatable);

    if (diff(prevProps.currentViewPoint, this.props.currentViewPoint)) {
      this.updateCurrentViewPoint();
    }

    if (diff(prevProps.lines, this.props.lines)) {
      this.updateLines();
    }

    if (diff(prevProps.popup, this.props.popup)) {
      this.updatePopup();
    }

    if (this.props.bounds && prevProps.bounds && !prevProps.bounds.equals(this.props.bounds)) {
      this.updateBounds();
    } else if (this.props.extents) {
      if (JSON.stringify(this.props.extents) !== JSON.stringify(prevProps.extents)) {
        this.updateExtents();
      }
    } else {
      let newCenter = null;
      let newZoom = null;

      if (JSON.stringify(prevProps.center) !== JSON.stringify(this.props.center)) {
        newCenter = this.props.center;
      }

      if (prevProps.zoom !== this.props.zoom) {
        newZoom = this.props.zoom;
      }

      if (newCenter && newZoom) {
        this.updateCenterAndZoom();
      } else if (newCenter) {
        this.updateCenter();
      } else if (newZoom) {
        this.updateZoom();
      }
    }

    if (prevProps.visible !== this.props.visible) {
      // this.updateVisibility();
    }

    this._layersLastUpdated = this.context.layerSettings.lastUpdated;
  }

  render() {
    const { className, editing } = this.props;
    const { showLayerPicker } = this.state;

    return (
      <span>
        <div
          ref={this.bindMapView}
          className={className}
        />
        <LayerPicker
          esriEnabled={false}
          editing={editing}
          show={showLayerPicker}
          onClose={this.handleLayersClose}
        />
      </span>
    );
  }

  bindMapView = (node) => {
    this.mapNode = node;
  };

  updateCenterAndZoom() {
    const center = this.props.center || MapView.defaultProps.center;
    this.map.setCenter({ lat: center[0], lng: center[1] });
    this.map.setZoom(this.props.zoom);
  }

  updateCenter() {
    const center = this.props.center || MapView.defaultProps.center;
    this.map.setCenter({ lat: center[0], lng: center[1] });
  }

  updateZoom() {
    this.map.setZoom(this.props.zoom);
  }

  updateExtents() {
    this.map.setCenter({ lat: this.props.extents.latitude, lng: this.props.extents.longitude });
    this.map.setZoom(this.props.extents.zoom);
  }

  updateDataExtents() {
    const { dataExtents: box } = this.props;

    let bounds;
    if (box && box.length === 4 && box.every((value) => value !== null)) {
      try {
        bounds = new google.maps.LatLngBounds(
          { lat: box[0], lng: box[1] },
          { lat: box[2], lng: box[3] },
        );
      } catch (error) {
        if (error.constructor.name === 'InvalidValueError' || error.name === 'InvalidValueError') {
          console.log('Invalid bounds coordinates provided:', box, error);
        } else {
          console.log('Unexpected error encountered setting bounds:', error);
        }
        return;
      }
    }

    if (box[0] === 0 && box[1] === 0) {
      return;
    }

    this.map.fitBounds(bounds);
  }

  updateBounds() {
    this.map.fitBounds(this.props.bounds);
  }

  updatePopup() {
    if (this.props.popup == null) {
      if (this.popup) {
        this.popup.setMap(null);
      }

      return;
    }

    if (this.props.popup.latitude != null && this.props.popup.longitude != null && this.props.popup.content != null) {
      const latLng = new google.maps.LatLng(this.props.popup.latitude, this.props.popup.longitude);

      if (this.popup) {
        this.popup.setMap(null);
      }

      this.popup = new this.Popup(latLng, this.props.popup.content);
      this.popup.setMap(this.map);
    }
  }

  updateLines() {
    if (this._lines) {
      for (const lineLayer of this._lines) {
        lineLayer.setMap(null);
      }
    }

    this._lines = null;

    if (!this.props.lines) {
      return;
    }

    this._lines = [];

    for (const featureCollection of this.props.lines.toArray()) {
      for (const feature of featureCollection.features) {
        const [firstPoint, lastPoint] = feature.geometry.coordinates;

        const polyline = new google.maps.Polyline({
          path: [
            { lat: firstPoint[1], lng: firstPoint[0] },
            { lat: lastPoint[1], lng: lastPoint[0] },
          ],
          strokeColor: '#eb1300',
          strokeOpacity: 1,
          strokeWeight: 4,
        });

        polyline.addListener('click', () => this.handleTrackLineClick(feature.properties.time));

        this._lines.push(polyline);

        polyline.setMap(this.map);
      }
    }
  }

  updateCircleMarkers() {
    if (!this.circleMarkers) {
      this.circleMarkers = [];
    }

    if (this.circleMarkers.length !== 0) {
      for (const marker of this.circleMarkers) {
        marker.setMap(null);
      }
    }

    if (!this.props.circleMarkers) {
      return;
    }

    for (const marker of this.props.circleMarkers.toArray()) {
      const markerOptions = marker.toJS();
      const color = markerOptions.color || '#F44336';

      const markerProps = {
        position: markerOptions.location,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          strokeColor: color,
          fillColor: color,
          fillOpacity: 0.4,
          strokeWeight: 2,
          scale: 10,
        },
        data: markerOptions.data,
        title: markerOptions.title,
      };

      const circle = new google.maps.Marker(markerProps);

      if (this.props.onMarkerClick) {
        circle.addListener('click', this.props.onMarkerClick);
      }

      if (this.props.onMarkerMouseOver) {
        circle.addListener('mouseover', () => {
          this.props.onMarkerMouseOver(circle);
        });
      }

      if (this.props.onMarkerMouseOut) {
        circle.addListener('mouseout', this.props.onMarkerMouseOut);
      }

      this.circleMarkers.push(circle);

      circle.setMap(this.map);
    }
  }

  updateCurrentViewPoint() {
    this.currentViewportMarkerManager.update(this.props.currentViewPoint);
  }

  get minZoom() {
    return 0;
  }

  get maxZoom() {
    return 22;
  }

  updateBasemap() {
    const { googleEngineActiveBasemap: activeBasemap } = this.context.layerSettings || SAFE_DEFAULT_BASEMAP;

    const mapTypeId = activeBasemap.type === 'system'
      ? google.maps.MapTypeId[activeBasemap.googleMapTypeId]
      : activeBasemap.id;

    this.map.setMapTypeId(mapTypeId);

    if (mapTypeId === 'osm') {
      this.osmAttributionControl.show();
    } else {
      this.osmAttributionControl.hide();
    }
  }

  async updateLayers() {
    const { googleEngineActiveBasemap } = this.context.layerSettings;

    if (!this._lastBaseMap || this._lastBaseMap.id !== googleEngineActiveBasemap.id) {
      this.updateBasemap();
      this._lastBaseMap = googleEngineActiveBasemap;
    }

    // In order to minimize the number of operations to the map, we have to find the difference between the
    // old layers and the new layers. We track the current layers and add any new ones and remove any current
    // layers that aren't in the new layer definition. If we use a naive approach here it will cause flicker
    // when we remove all the layers and add them back.

    const layersToAdd = {};
    const layersToRemove = {};
    const layers = {};
    const layersOrdered = [];
    const { default: UtfGrid } = await this.UtfGrid;

    if (!this._activeLayers) {
      this._activeLayers = {};
      this._activeLayerObjects = {};
    }

    const { context: { layerSettings: { activeLayersImmutable } } } = this;
    const layerCollection = activeLayersImmutable.toArray().reverse().map((o) => o.toJS());

    for (const layer of layerCollection) {
      const key = JSON.stringify(layer);

      layers[key] = layer;
      layersOrdered.push(key);

      if (!this._activeLayers[key]) {
        layersToAdd[key] = layer;
      }
    }

    const layerObjectsToRemove = {};
    this.layerManager.types.forEach((type) => {
      layerObjectsToRemove[type] = {};
    });

    for (const key of Object.keys(this._activeLayers)) {
      if (!layers[key]) {
        layersToRemove[key] = this._activeLayers[key];

        const layerJSON = JSON.parse(key);
        const layerType = layerJSON.type;
        layerObjectsToRemove[layerType][key] = this._activeLayers[key];
      }
    }

    const layerObjectsToAdd = {};
    this.layerManager.types.forEach((type) => {
      layerObjectsToAdd[type] = {};
    });

    for (const key of Object.keys(layersToAdd)) {
      const layer = layersToAdd[key];
      switch (layer.type) {
        case 'xyz': {
          const tileLayer = new google.maps.ImageMapType({
            getTileUrl: getTileUrl(layer.url),
            tileSize: new google.maps.Size(256, 256),
            maxZoom: this.maxZoom,
            minZoom: this.minZoom,
          });
          layerObjectsToAdd[layer.type][key] = [tileLayer];
          break;
        }
        case 'wms': {
          const tileLayer = new google.maps.ImageMapType({
            getTileUrl: getWmsTileUrl(layer.url),
            tileSize: new google.maps.Size(256, 256),
            maxZoom: this.maxZoom,
            minZoom: this.minZoom,
          });
          layerObjectsToAdd[layer.type][key] = [tileLayer];
          break;
        }
        case 'mbtiles': {
          if (layer?.data?.tileJSON) {
            const tileLayer = new google.maps.ImageMapType({
              getTileUrl: getTileUrl(fulcrumMBTilesPNGLayer(layer, this.context.api.token)),
              tileSize: new google.maps.Size(256, 256),
              maxZoom: this.maxZoom, // Should we use layer.data.tileJSON.minzoom/maxzoom?
              minZoom: this.minZoom,
            });

            if (layer.data.tileJSON.template) {
              const { template } = layer.data.tileJSON;
              const gridLayer = new UtfGrid(fulcrumMBTilesJSONLayer(layer, this.context.api.token));

              const clickHandler = (event) => {
                this.handleMBTileGridClick(event.data, template);
              };

              const mouseoverHandler = (event) => {
                this.handleMBTileGridOn(event.data, template);
              };

              gridLayer.on('click', clickHandler);
              gridLayer.on('mouseover', mouseoverHandler);
              gridLayer.on('mouseout', this.handleMBTileGridOff);

              layerObjectsToAdd[layer.type][key] = [tileLayer, gridLayer];
            } else {
              layerObjectsToAdd[layer.type][key] = [tileLayer];
            }
          } else if (layer.data == null) {
            this.context.layerSettings.fetchTileJSON(layer, this.context.api.token);
          }

          break;
        }
        case 'geojson': {
          if (layer.geojson) {
            layerObjectsToAdd[layer.type][key] = [layer.geojson];
          } else if (layer.url && layer.data) {
            if (layer.data.json) {
              layerObjectsToAdd[layer.type][key] = [layer.data.json];
            }
          } else if (layer.url) {
            this.context.layerSettings.fetchGeoJSON(layer);
          }

          break;
        }
        case 'data': {
          const tileLayer = new google.maps.ImageMapType({
            getTileUrl: getTileUrl(fulcrumPNGLayer(layer, this.context.api.token)),
            tileSize: new google.maps.Size(256, 256),
            maxZoom: this.maxZoom,
            minZoom: this.minZoom,
          });

          const gridLayer = new UtfGrid(fulcrumJSONLayer(layer, this.context.api.token));

          gridLayer.on('click', this.handleTileGridClick, gridLayer);
          gridLayer.on('mouseover', this.handleTileGridOn, gridLayer);
          gridLayer.on('mouseout', this.handleTileGridOff, gridLayer);

          layerObjectsToAdd[layer.type][key] = [tileLayer, gridLayer];

          break;
        }
        case 'fulcrum': {
          const tileLayer = new google.maps.ImageMapType({
            getTileUrl: getTileUrl(fulcrumPNGLayer(layer, this.context.api.token)),
            tileSize: new google.maps.Size(256, 256),
            maxZoom: this.maxZoom,
            minZoom: this.minZoom,
          });

          layerObjectsToAdd[layer.type][key] = [tileLayer];

          break;
        }
        default:
          break;
      }
    }

    this.gridControl.hide();
    this.layerManager.removeLayers(layerObjectsToRemove);
    this.addLayers(layerObjectsToAdd);

    this.layerManager.layerOrder = layersOrdered;

    for (const key of Object.keys(layersToRemove)) {
      delete this._activeLayers[key];
    }
  }

  addLayers(layerObjects) {
    Object.keys(layerObjects).forEach((type) => {
      const layerTypeObject = layerObjects[type];

      Object.keys(layerTypeObject).forEach((key) => {
        this._activeLayers[key] = layerTypeObject[key];
      });
    });

    this.layerManager.addLayers(layerObjects);
  }

  handleMapClicked = (event) => {
    if (this.props.onMapClick) {
      this.props.onMapClick(event);
    }
  };

  handleMapMoveEnd = () => {
    if (this.props.onMapMoveEnd) {
      const center = this.map.getCenter();
      const zoom = this.map.getZoom();
      const bounds = this.map.getBounds();

      this.props.onMapMoveEnd(center, zoom, bounds);
    }
  };

  handleTileGridClick = (event) => {
    if (event.data && this.props.onFeatureClick) {
      this.props.onFeatureClick(event.data.id, event.data.record_id ?? event.data.id, event.data.parent_id);
    }
  };

  handleTileGridOn = (event) => {
    if (event) {
      // TODO: Rework UtfGrid so the .stop() method is available here
      //       so that the gridControl hides, and stays hidden.
      // this.gridControl.hide();

      if (event.data) {
        const { id } = event.data;

        if (this.hoverID !== id) {
          if (this.props.onFeatureMouseOver) {
            this.hoverID = id;
            this.props.onFeatureMouseOver(event.data.id, event.data.record_id ?? event.data.id, event.data.parent_id);
          }
        }

        this.map.setOptions({ draggableCursor: 'pointer' });
      }
    }
  };

  handleTileGridOff = (event) => {
    this.hoverID = null;
    this.map.setOptions({ draggableCursor: this.props.cursor });

    if (this.props.onFeatureMouseOut) {
      this.props.onFeatureMouseOut();
    }
  };

  handleTrackLineClick = (time) => {
    if (this.props.onTrackLineClick) {
      this.props.onTrackLineClick(time);
    }
  };

  showLayerPicker() {
    this.setState({ showLayerPicker: true });
  }

  hideLayerPicker() {
    this.setState({ showLayerPicker: false });
  }

  handleExtentsClick = (event) => {
    if (this.props.dataExtents) {
      this.updateDataExtents();
    }
  };

  handleZoomInClick = () => {
    this.map.setZoom(this.map.getZoom() + 1);
  };

  handleZoomOutClick = () => {
    this.map.setZoom(this.map.getZoom() - 1);
  };

  handleLayerClick = (event) => {
    this.showLayerPicker();
  };

  handleLayersClose = async () => {
    const { layerSettings } = this.context;

    await mapLayersAppliedEvent(
      'Google',
      layerSettings.googleEngineActiveBasemap.id,
      layerSettings.activeOverlays,
    );

    this.hideLayerPicker();
  };

  handleLocateClick = async (event) => {
    if (!('geolocation' in navigator)) {
      return;
    }

    if (this._watchingLocation) {
      this.locateControl.setIcon(LocateControl.icon);
      this._watchingLocation = false;

      if (this._geolocationMarker) {
        this._geolocationMarker.setMap(null);
        this._geolocationMarker = null;
      }
    } else {
      this.locateControl.setIcon(LocateControl.iconActive);
      this._watchingLocation = true;
      const GeoLocationMarker = await this.GeolocationMarker.default;
      this._geolocationMarker = new GeoLocationMarker();

      google.maps.event.addListenerOnce(this._geolocationMarker, 'position_changed', () => {
        const center = this._geolocationMarker.getPosition();

        if (center) {
          this.map.setCenter(center);
        }
      });

      this._geolocationMarker.setMap(this.map);
    }
  };

  handleMBTileGridClick = (data, template) => {
    this.gridControl.setData(data, template, 'full');
  };

  handleMBTileGridOn = (data, template) => {
    this.map.setOptions({ draggableCursor: 'pointer' });
    this.gridControl.setData(data, template, 'teaser');
  };

  handleMBTileGridOff = (event) => {
    this.map.setOptions({ draggableCursor: '' });

    if (!this.gridControl.pinned) {
      this.gridControl.hide();
    }
  };
}
